import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { Translate, withTranslate } from 'core/translate';
import { SvgIcon } from 'core/icons';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalFooterButtons,
  ModalFooterCancelButton,
  ModalFooterPrimaryButton,
  ModalHeader
} from '../../common/modal/Modal';
import { getAllByBrandAndCountry } from '../../resources/localStorage/localStorage.service';
import ShareDropDown from './ShareDropDown';
import ShareDetails from './ShareDetails';
import ShareTabs from './ShareTabs';
import LinkForm from './LinkForm';
import { withModelInfo } from '../../common/ModelInfoContext';
import fetchIncentives from '../../api/fetchIncentives';
import { fetchByStyleIdAndSerializedState } from '../../api/fetchConfig';

export const EMAIL_TAB_INDEX = 0;

const Loading = () => (
  <div className="save-send__modal-overlay">
    <div className="save-send__modal-overlay-spinner-wrapper">
      <SvgIcon name="spinner" className="byo-loading__spinner" />
    </div>
  </div>
);

class Share extends React.Component {
  constructor(props) {
    super(props);
    const { bodyStyle, country, brand, year } = props.modelInfo;

    const savedConfigs = getAllByBrandAndCountry(brand, country);
    const predicate = x => x.bodystyle === bodyStyle && x.modelyear === year;
    const selected = _.find(savedConfigs, predicate);
    const rest = _.without(savedConfigs, selected);
    const orderedConfigs = [selected, ...rest];

    this.state = {
      configs: orderedConfigs,
      selected: orderedConfigs[0],
      config: null,
      incentives: null,
      isLoading: true,
      to: '',
      message: '',
      copySuccessMessage: '',
      tabIndex: EMAIL_TAB_INDEX,
      isSendingEmail: false,
      isSendError: false,
      isSendSuccess: false,
      isCopySupported:
        document.queryCommandSupported && document.queryCommandSupported('copy')
    };

    this.copyRef = React.createRef();

    this.onDropDownSelect = this.onDropDownSelect.bind(this);
    this.onDetailsSelect = this.onDetailsSelect.bind(this);
    this.onToChange = this.onToChange.bind(this);
    this.onMessageChange = this.onMessageChange.bind(this);
    this.onSelectTab = this.onSelectTab.bind(this);
    this.handleSend = this.handleSend.bind(this);
    this.handleCopy = this.handleCopy.bind(this);
  }

  componentDidMount() {
    this.fetchSelectedConfig();
  }

  onClose = () => {
    this.props.history.push('/summary');
  };

  onDropDownSelect(selected) {
    this.setState({ selected, isLoading: true }, () => {
      this.fetchSelectedConfig();
    });
  }

  fetchSelectedConfig() {
    const { postalCode, modelInfo } = this.props;
    const { selected } = this.state;

    const {
      brand,
      country,
      lang,
      modelyear,
      carline,
      bodystyle,
      styleId,
      ss
    } = selected;

    const options = {
      year: modelyear,
      carline,
      bodyStyle: bodystyle,
      postalCode
    };

    fetchByStyleIdAndSerializedState(styleId, ss, options)
      .then(config => {
        return fetchIncentives({
          servicesPath: modelInfo.servicesPath,
          modelPath: `${lang}/${country}/${brand}/${carline}/${modelyear}/${bodystyle}`,
          styleId,
          postalCode,
          rpos: config.selections.rpos
        }).then(incentives => ({ config, incentives }));
      })
      .then(
        ({ config, incentives }) => {
          this.setState({
            isLoading: false,
            config,
            incentives
          });
        },
        () => {
          this.setState({
            isLoading: false
          });
        }
      );
  }

  onDetailsSelect() {
    const { configs, config, selected } = this.state;
    if (config.ss === configs[0].ss) {
      this.onClose();
    } else {
      window.location.href = selected.originUrl;
    }
  }

  onToChange(to) {
    this.setState({ to });
  }

  onMessageChange(message) {
    this.setState({ message });
  }

  onSelectTab(tabIndex) {
    this.setState({ tabIndex });
  }

  handleSend() {
    const { modelInfo } = this.props;
    const { selected, message, to } = this.state;
    const { originUrl, mobileUrl, brand, country, lang } = selected;

    const data = {
      to,
      message,
      brand: `${lang}_${country}_${brand}`,
      url: originUrl,
      mobileUrl: mobileUrl
    };

    this.setState(
      {
        isSendingEmail: true,
        isSendSuccess: false,
        isSendError: false
      },
      () => {
        fetch(`${modelInfo.legacyServicesPath}/email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8'
          },
          body: JSON.stringify(data)
        })
          .then(response => {
            const json = response.json();
            if (response.status === 200) {
              return json;
            } else {
              return json.then(err => {
                throw err;
              });
            }
          })
          .then(
            () => {
              this.setState({
                to: '',
                message: '',
                isSendingEmail: false,
                isSendSuccess: true
              });
            },
            () => {
              this.setState({
                isSendingEmail: false,
                isSendError: true
              });
            }
          );
      }
    );
  }

  handleCopy() {
    const { translate } = this.props;
    const input = this.copyRef.current;
    let success;

    try {
      input.select();
      success = document.execCommand('copy');
    } catch (e) {
      success = false;
    }

    this.setState({
      copySuccessMessage: translate(success ? 'copy.url' : 'select.url')
    });
  }

  render() {
    const { translate, appResources } = this.props;
    const {
      configs,
      selected,
      isLoading,
      isSendingEmail,
      config,
      incentives,
      tabIndex,
      isCopySupported,
      copySuccessMessage
    } = this.state;

    const title = translate('component.sendsave.your.saved.vehicle');
    const {
      isFamilyFirstUS,
      year,
      title: vehicleTitle,
      isLiteRenderMode
    } = this.props.modelInfo;
    const { originUrl } = selected;

    return (
      <Modal
        isOpen={true}
        className="save-send-modal"
        contentLabel={title}
        onRequestClose={this.onClose}>
        <ModalHeader text={title} onClose={this.onClose} />
        <ModalBody className={isLoading ? 'modal-body--hide-overflow' : ''}>
          {isLoading ? <Loading /> : null}
          <ShareDropDown
            configs={configs}
            selected={selected}
            onSelect={this.onDropDownSelect}
          />
          <div className="modal__content modal__content--no-padding row">
            <div className="columns save-send">
              <ShareDetails
                config={config}
                incentives={incentives}
                appResources={appResources}
                lastModified={selected._lastModified}
                year={selected.modelyear}
                bodyStyleTitle={selected.bodyStyleTitle}
                country={selected.country}
                isFamilyFirstUS={isFamilyFirstUS}
                onSelect={this.onDetailsSelect}
                tabsContent={
                  <ShareTabs
                    selectedIndex={tabIndex}
                    onSelect={this.onSelectTab}
                    emailContent={
                      <div className="save-send__email-link">
                        <Translate value="mail.link.message" />
                      </div>
                    }
                    linkContent={
                      <LinkForm
                        url={selected.originUrl}
                        message={copySuccessMessage}
                        copyRef={this.copyRef}
                      />
                    }
                    isLiteRenderMode={isLiteRenderMode}
                  />
                }
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalFooterButtons>
            {isLiteRenderMode ? (
              <ModalFooterPrimaryButton
                onClick={this.handleCopy}
                disabled={isLoading}
                data-dtm={title}>
                <Translate value={isCopySupported ? 'copy' : 'select.link'} />
              </ModalFooterPrimaryButton>
            ) : (
              <Fragment>
                {tabIndex === EMAIL_TAB_INDEX ? (
                  <Fragment>
                    <ModalFooterCancelButton
                      onClick={this.onClose}
                      data-dtm={title}
                    />
                    <ModalFooterPrimaryButton
                      as={'a'}
                      target="_blank"
                      href={`mailto:?subject=${translate('mail.link.subject', {
                        brand: translate('price.summary.brand'),
                        carline: vehicleTitle
                      })}&body=${translate('mail.link.body', {
                        year,
                        brand: translate('price.summary.brand'),
                        carline: vehicleTitle,
                        url: encodeURIComponent(originUrl)
                      })}`}
                      disabled={isLoading || isSendingEmail}
                      data-dtm={title}>
                      <Translate value="mail.link.button.label" />
                    </ModalFooterPrimaryButton>
                  </Fragment>
                ) : (
                  <ModalFooterPrimaryButton
                    onClick={this.handleCopy}
                    disabled={isLoading}
                    data-dtm={title}>
                    <Translate
                      value={isCopySupported ? 'copy' : 'select.link'}
                    />
                  </ModalFooterPrimaryButton>
                )}
              </Fragment>
            )}
          </ModalFooterButtons>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  postalCode: state.params.postalCode,
  appResources: state.appResources
});

const mergeProps = stateProps => {
  return {
    ...stateProps
  };
};

export default withRouter(
  withTranslate(withModelInfo(connect(mapStateToProps, mergeProps)(Share)))
);
